/**
 * Chat Sidebar Component
 *
 * Conversation history in the Kampagnen-Builder-v2 look: top-aligned list,
 * folders, archive section and a context menu (right-click or ⋯) with
 * rename / move to folder / archive / delete. Folders can be renamed.
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  ClickAwayListener,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { dc } from '../design-tokens';

export interface ChatConversation {
  id: string;
  title: string;
  lastMessage?: string;
  updatedAt: Date;
  isActive?: boolean;
  folder?: string | null;
  isArchived?: boolean;
}

interface ChatSidebarProps {
  conversations: ChatConversation[];
  activeConversationId?: string;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onRenameConversation?: (id: string, newTitle: string) => void;
  onDeleteConversation?: (id: string) => void;
  onMoveToFolder?: (id: string, folder: string | null) => void;
  onArchiveConversation?: (id: string, archived: boolean) => void;
  onRenameFolder?: (from: string, to: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface MenuState {
  anchorEl?: HTMLElement;
  /** Bei Rechtsklick: Mausposition statt Anker-Element */
  position?: { top: number; left: number };
  conversation?: ChatConversation;
  folder?: string;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  activeConversationId,
  onNewChat,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation,
  onMoveToFolder,
  onArchiveConversation,
  onRenameFolder,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  const [menu, setMenu] = React.useState<MenuState | null>(null);
  const [folderSubmenuAnchor, setFolderSubmenuAnchor] = React.useState<HTMLElement | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingFolder, setEditingFolder] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const [newFolderFor, setNewFolderFor] = React.useState<string | null>(null);
  const [collapsedFolders, setCollapsedFolders] = React.useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = React.useState('');
  const [editError, setEditError] = React.useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = React.useState(false);
  const editInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if ((editingId || editingFolder || newFolderFor) && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId, editingFolder, newFolderFor]);

  const query = searchQuery.trim().toLowerCase();
  const matchesQuery = (c: ChatConversation) =>
    !query || c.title.toLowerCase().includes(query) || (c.folder || '').toLowerCase().includes(query);

  const active = conversations.filter((c) => !c.isArchived && matchesQuery(c));
  const archived = conversations.filter((c) => c.isArchived && matchesQuery(c));
  const folders = Array.from(
    new Set(active.map((c) => c.folder).filter((f): f is string => !!f))
  ).sort((a, b) => a.localeCompare(b));
  const unfiled = active.filter((c) => !c.folder);

  const formatDate = (date: Date) => {
    const now = new Date();
    const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return t('today', 'Heute');
    if (days === 1) return t('yesterday', 'Gestern');
    if (days < 7) return t('daysAgo', 'vor {{days}} Tagen', { days });
    return date.toLocaleDateString();
  };

  const closeMenu = () => {
    setMenu(null);
    setFolderSubmenuAnchor(null);
  };

  const openConversationMenu = (
    conv: ChatConversation,
    e: React.MouseEvent<HTMLElement>,
    fromContextMenu: boolean
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu(
      fromContextMenu
        ? { position: { top: e.clientY, left: e.clientX }, conversation: conv }
        : { anchorEl: e.currentTarget, conversation: conv }
    );
  };

  const openFolderMenu = (folder: string, e: React.MouseEvent<HTMLElement>, fromContextMenu: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu(
      fromContextMenu
        ? { position: { top: e.clientY, left: e.clientX }, folder }
        : { anchorEl: e.currentTarget, folder }
    );
  };

  const startRenameConversation = (conv: ChatConversation) => {
    closeMenu();
    setEditingId(conv.id);
    setEditValue(conv.title);
  };

  const startRenameFolder = (folder: string) => {
    closeMenu();
    setEditingFolder(folder);
    setEditValue(folder);
  };

  const confirmEdit = () => {
    const value = editValue.trim();
    if (editingFolder && value && value !== editingFolder) {
      // Zielname darf nicht mit einem bestehenden Ordner kollidieren —
      // sonst würden die Ordner stillschweigend zusammengelegt. Gegen ALLE
      // Ordner prüfen, nicht nur die aktuell durch die Suche sichtbaren.
      const allFolders = Array.from(
        new Set(conversations.map((c) => c.folder).filter((f): f is string => !!f))
      );
      const exists = allFolders.some(
        (f) => f !== editingFolder && f.toLowerCase() === value.toLowerCase()
      );
      if (exists) {
        setEditError(
          isDe
            ? `Ein Ordner „${value}" existiert bereits.`
            : `A folder "${value}" already exists.`
        );
        return;
      }
      if (onRenameFolder) onRenameFolder(editingFolder, value);
    }
    if (editingId && value && onRenameConversation) {
      onRenameConversation(editingId, value);
    }
    if (newFolderFor && value && onMoveToFolder) {
      onMoveToFolder(newFolderFor, value);
    }
    setEditingId(null);
    setEditingFolder(null);
    setNewFolderFor(null);
    setEditValue('');
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingFolder(null);
    setNewFolderFor(null);
    setEditValue('');
    setEditError(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const toggleFolder = (folder: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  };

  const renderEditRow = () => (
    <ClickAwayListener onClickAway={confirmEdit}>
      <Box className={classes.editContainer}>
        <TextField
          inputRef={editInputRef}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            setEditError(null);
          }}
          onKeyDown={handleEditKeyDown}
          variant="outlined"
          size="small"
          fullWidth
          inputProps={{ maxLength: 200 }}
          className={classes.editInput}
          placeholder={newFolderFor ? (isDe ? 'Ordnername' : 'Folder name') : undefined}
          error={!!editError}
          helperText={editError || undefined}
        />
      </Box>
    </ClickAwayListener>
  );

  const renderConversation = (conv: ChatConversation, indented: boolean) => {
    if (editingId === conv.id) return <React.Fragment key={conv.id}>{renderEditRow()}</React.Fragment>;
    return (
      <Box
        key={conv.id}
        className={cx(
          classes.conversationItem,
          indented && 'indented',
          conv.id === activeConversationId && 'selected'
        )}
        onClick={() => onSelectConversation(conv.id)}
        onContextMenu={(e) => openConversationMenu(conv, e, true)}
      >
        <ChatBubbleOutlineIcon className={classes.conversationIcon} />
        <Box className={classes.conversationText}>
          <Typography variant="body2" className={classes.conversationTitle}>
            {conv.title}
          </Typography>
          <Typography variant="caption" className={classes.conversationDate}>
            {formatDate(conv.updatedAt)}
          </Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.moreButton}
          onClick={(e) => openConversationMenu(conv, e, false)}
          aria-label={t('moreOptions', 'Mehr Optionen')}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  if (isCollapsed) {
    return (
      <Box className={cx(classes.sidebar, 'collapsed')}>
        <Box className={classes.collapsedHeader}>
          <Tooltip title={t('expandSidebar', 'Seitenleiste öffnen')} placement="right">
            <IconButton onClick={onToggleCollapse} className={classes.collapseButton}>
              <MenuOpenIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title={t('newChat', 'Neuer Chat')} placement="right">
          <IconButton onClick={onNewChat} className={classes.collapsedNewChat}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  const menuConv = menu?.conversation;
  const menuFolder = menu?.folder;

  return (
    <Box className={classes.sidebar}>
      {/* Header: Neuer Chat + Einklappen — kompakt, kein Abstand zur Liste */}
      <Box className={classes.header}>
        <Button startIcon={<AddIcon />} onClick={onNewChat} fullWidth className={classes.newChatButton}>
          {t('newChat', 'Neuer Chat')}
        </Button>
        {onToggleCollapse && (
          <Tooltip title={t('collapseSidebar', 'Seitenleiste einklappen')}>
            <IconButton onClick={onToggleCollapse} className={classes.collapseButton}>
              <MenuOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Suche */}
      <Box className={classes.searchRow}>
        <SearchIcon className={classes.searchIcon} />
        <input
          className={classes.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={isDe ? 'Chats durchsuchen…' : 'Search chats…'}
          aria-label={isDe ? 'Chats durchsuchen' : 'Search chats'}
        />
      </Box>

      {/* Liste — beginnt direkt unter dem Header */}
      <Box className={classes.conversationList}>
        {active.length === 0 && archived.length === 0 ? (
          <Box className={classes.emptyState}>
            <ChatBubbleOutlineIcon className={classes.emptyIcon} />
            <Typography variant="body2" align="center" className={classes.emptyText}>
              {query
                ? isDe ? 'Keine Treffer' : 'No matches'
                : t('noConversations', 'Noch keine Unterhaltungen')}
            </Typography>
            <Typography variant="caption" align="center" className={classes.emptyHint}>
              {query
                ? isDe ? 'Andere Suche versuchen' : 'Try a different search'
                : t('startNewChat', 'Starte einen neuen Chat')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Ordner */}
            {folders.map((folder) => {
              const items = active.filter((c) => c.folder === folder);
              const isFolderCollapsed = collapsedFolders.has(folder);
              return (
                <Box key={folder}>
                  {editingFolder === folder ? (
                    renderEditRow()
                  ) : (
                    <Box
                      className={classes.folderHeader}
                      onClick={() => toggleFolder(folder)}
                      onContextMenu={(e) => openFolderMenu(folder, e, true)}
                    >
                      {isFolderCollapsed ? (
                        <ChevronRightIcon className={classes.folderChevron} />
                      ) : (
                        <ExpandMoreIcon className={classes.folderChevron} />
                      )}
                      {isFolderCollapsed ? (
                        <FolderOutlinedIcon className={classes.folderIcon} />
                      ) : (
                        <FolderOpenOutlinedIcon className={classes.folderIcon} />
                      )}
                      <Typography variant="body2" className={classes.folderName}>
                        {folder}
                      </Typography>
                      <Typography variant="caption" className={classes.folderCount}>
                        {items.length}
                      </Typography>
                      <IconButton
                        size="small"
                        className={classes.moreButton}
                        onClick={(e) => openFolderMenu(folder, e, false)}
                        aria-label={t('moreOptions', 'Mehr Optionen')}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {!isFolderCollapsed && items.map((conv) => renderConversation(conv, true))}
                </Box>
              );
            })}

            {/* Chats ohne Ordner */}
            {unfiled.map((conv) => renderConversation(conv, false))}
            {newFolderFor && renderEditRow()}

            {/* Archiv */}
            {archived.length > 0 && (
              <>
                <Divider className={classes.archiveDivider} />
                <Box className={classes.folderHeader} onClick={() => setArchiveOpen((v) => !v)}>
                  {archiveOpen ? (
                    <ExpandMoreIcon className={classes.folderChevron} />
                  ) : (
                    <ChevronRightIcon className={classes.folderChevron} />
                  )}
                  <ArchiveOutlinedIcon className={classes.folderIcon} />
                  <Typography variant="body2" className={classes.folderName}>
                    {isDe ? 'Archiv' : t('archive', 'Archive')}
                  </Typography>
                  <Typography variant="caption" className={classes.folderCount}>
                    {archived.length}
                  </Typography>
                </Box>
                {archiveOpen && archived.map((conv) => renderConversation(conv, true))}
              </>
            )}
          </>
        )}
      </Box>

      {/* Kontextmenü für Chats */}
      <Menu
        open={!!menu && !!menuConv}
        onClose={closeMenu}
        anchorEl={menu?.anchorEl}
        anchorReference={menu?.position ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menu?.position}
        classes={{ paper: classes.menuPaper }}
      >
        {onRenameConversation && (
          <MenuItem onClick={() => menuConv && startRenameConversation(menuConv)}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            {isDe ? 'Umbenennen' : t('rename', 'Rename')}
          </MenuItem>
        )}
        {onMoveToFolder && !menuConv?.isArchived && (
          <MenuItem onClick={(e) => setFolderSubmenuAnchor(e.currentTarget)}>
            <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
            {isDe ? 'In Ordner verschieben' : t('moveToFolder', 'Move to folder')}
            <ChevronRightIcon fontSize="small" className={classes.submenuArrow} />
          </MenuItem>
        )}
        {onArchiveConversation && (
          <MenuItem
            onClick={() => {
              if (menuConv) onArchiveConversation(menuConv.id, !menuConv.isArchived);
              closeMenu();
            }}
          >
            <ListItemIcon>
              {menuConv?.isArchived
                ? <UnarchiveOutlinedIcon fontSize="small" />
                : <ArchiveOutlinedIcon fontSize="small" />}
            </ListItemIcon>
            {menuConv?.isArchived
              ? isDe ? 'Wiederherstellen' : t('unarchive', 'Unarchive')
              : isDe ? 'Archivieren' : t('archiveAction', 'Archive')}
          </MenuItem>
        )}
        {onDeleteConversation && (
          <MenuItem
            className={classes.deleteMenuItem}
            onClick={() => {
              if (menuConv) onDeleteConversation(menuConv.id);
              closeMenu();
            }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" className={classes.deleteIcon} /></ListItemIcon>
            {isDe ? 'Löschen' : t('delete', 'Delete')}
          </MenuItem>
        )}
      </Menu>

      {/* Untermenü: Ordner-Auswahl */}
      <Menu
        open={!!folderSubmenuAnchor}
        onClose={() => setFolderSubmenuAnchor(null)}
        anchorEl={folderSubmenuAnchor}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.menuPaper }}
      >
        {folders
          .filter((f) => f !== menuConv?.folder)
          .map((folder) => (
            <MenuItem
              key={folder}
              onClick={() => {
                if (menuConv && onMoveToFolder) onMoveToFolder(menuConv.id, folder);
                closeMenu();
              }}
            >
              <ListItemIcon><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
              {folder}
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => {
            if (menuConv) {
              setNewFolderFor(menuConv.id);
              setEditValue('');
            }
            closeMenu();
          }}
        >
          <ListItemIcon><CreateNewFolderOutlinedIcon fontSize="small" /></ListItemIcon>
          {isDe ? 'Neuer Ordner…' : t('newFolder', 'New folder…')}
        </MenuItem>
        {menuConv?.folder && (
          <MenuItem
            onClick={() => {
              if (menuConv && onMoveToFolder) onMoveToFolder(menuConv.id, null);
              closeMenu();
            }}
          >
            <ListItemIcon><CloseFolderIcon /></ListItemIcon>
            {isDe ? 'Aus Ordner entfernen' : t('removeFromFolder', 'Remove from folder')}
          </MenuItem>
        )}
      </Menu>

      {/* Kontextmenü für Ordner */}
      <Menu
        open={!!menu && !!menuFolder}
        onClose={closeMenu}
        anchorEl={menu?.anchorEl}
        anchorReference={menu?.position ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menu?.position}
        classes={{ paper: classes.menuPaper }}
      >
        {onRenameFolder && (
          <MenuItem onClick={() => menuFolder && startRenameFolder(menuFolder)}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            {isDe ? 'Ordner umbenennen' : t('renameFolder', 'Rename folder')}
          </MenuItem>
        )}
        {onMoveToFolder && (
          <MenuItem
            onClick={() => {
              if (menuFolder) {
                active
                  .filter((c) => c.folder === menuFolder)
                  .forEach((c) => onMoveToFolder(c.id, null));
              }
              closeMenu();
            }}
          >
            <ListItemIcon><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
            {isDe ? 'Ordner auflösen' : t('dissolveFolder', 'Dissolve folder')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

/** Kleines "Ordner verlassen"-Icon (Ordner mit Minus gibt es nicht in MUI-Outlined) */
const CloseFolderIcon: React.FC = () => (
  <FolderOutlinedIcon fontSize="small" style={{ opacity: 0.55 }} />
);

const useStyles = makeStyles()((theme) => ({
  sidebar: {
    // Panel-Look wie die übrigen Bereichs-Panels (Kontextpanel): 248px, weiß.
    width: 248,
    height: '100%',
    backgroundColor: '#FFFFFF',
    borderRight: '1px solid #E3E3E8',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    '&.collapsed': {
      width: 52,
    },
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing(1.25, 1.25, 0.75, 1.25),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  collapsedHeader: {
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
  },
  newChatButton: {
    flex: 1,
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    borderRadius: 10,
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    color: dc.ink,
    padding: theme.spacing(0.75, 1.5),
    '&:hover': {
      borderColor: dc.primary,
      color: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  collapsedNewChat: {
    margin: '0 auto',
    color: dc.muted,
    '&:hover': {
      backgroundColor: '#F5F5FB',
    },
  },
  collapseButton: {
    color: dc.muted,
    '&:hover': {
      color: dc.ink,
      backgroundColor: '#F5F5FB',
    },
  },
  searchRow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    margin: theme.spacing(0.25, 1.25, 0.5, 1.25),
    padding: theme.spacing(0.6, 1.1),
    borderRadius: 10,
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    '&:focus-within': {
      borderColor: dc.primary,
    },
  },
  searchIcon: {
    fontSize: '1rem',
    color: dc.faint,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: '0.8rem',
    color: dc.ink,
    '&::placeholder': {
      color: dc.faint,
    },
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(0.5, 1, 1.5, 1),
    scrollbarWidth: 'thin',
    scrollbarColor: '#C0C9E0 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#C9D2E8',
      borderRadius: 999,
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4, 2),
    gap: theme.spacing(0.75),
  },
  emptyIcon: {
    fontSize: 40,
    color: dc.faint,
    marginBottom: theme.spacing(0.5),
  },
  emptyText: {
    color: dc.ink,
    fontWeight: 600,
  },
  emptyHint: {
    color: dc.muted,
  },
  folderHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: theme.spacing(0.75, 0.75),
    borderRadius: 9,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#F5F5FB',
    },
    '&:hover .MuiIconButton-root': {
      opacity: 1,
    },
  },
  folderChevron: {
    fontSize: '1rem',
    color: dc.faint,
    flexShrink: 0,
  },
  folderIcon: {
    fontSize: '1.05rem',
    color: dc.muted,
    flexShrink: 0,
  },
  folderName: {
    flex: 1,
    fontWeight: 700,
    fontSize: '0.8rem',
    color: dc.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  folderCount: {
    color: dc.faint,
    fontSize: '0.7rem',
    fontWeight: 600,
    flexShrink: 0,
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: theme.spacing(0.9, 1),
    borderRadius: 10,
    cursor: 'pointer',
    marginBottom: 2,
    border: '1px solid transparent',
    '&.indented': {
      marginLeft: theme.spacing(2),
    },
    '&.selected': {
      backgroundColor: dc.surface,
      border: `1px solid ${dc.border}`,
      boxShadow: '0 2px 8px rgba(27,37,89,0.05)',
    },
    '&:hover': {
      backgroundColor: '#F5F5FB',
    },
    '&:hover .MuiIconButton-root': {
      opacity: 1,
    },
  },
  conversationIcon: {
    fontSize: '1rem',
    color: dc.muted,
    flexShrink: 0,
  },
  conversationText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  conversationTitle: {
    fontWeight: 600,
    fontSize: '0.82rem',
    color: dc.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.35,
  },
  conversationDate: {
    fontSize: '0.65rem',
    color: dc.faint,
    lineHeight: 1.3,
  },
  moreButton: {
    padding: 2,
    color: dc.muted,
    opacity: 0,
    transition: 'opacity 0.15s',
    flexShrink: 0,
    '&:hover': {
      color: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  menuPaper: {
    borderRadius: 12,
    border: `1px solid ${dc.border}`,
    boxShadow: dc.shadow,
    minWidth: 200,
    '& .MuiMenuItem-root': {
      fontSize: '0.85rem',
      color: dc.ink,
      gap: 4,
      '&:hover': {
        backgroundColor: dc.primaryHoverBg,
      },
    },
    '& .MuiListItemIcon-root': {
      minWidth: 30,
      color: dc.muted,
    },
  },
  submenuArrow: {
    marginLeft: 'auto',
    color: dc.faint,
  },
  deleteMenuItem: {
    color: `${theme.palette.error.main} !important`,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  archiveDivider: {
    margin: theme.spacing(1, 0.5),
    borderColor: dc.border,
  },
  editContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 0.5),
    marginBottom: 2,
  },
  editInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dc.surface,
      borderRadius: 10,
      '& fieldset': {
        borderColor: dc.primary,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(0.75, 1),
    },
  },
}));

export default ChatSidebar;
